/* ==========================================================================
   Custom Page Transitions
   --------------------------------------------------------------------------
   Barba.js + GSAPによるページ遷移。
   - ブランドカラーの幕が下からせり上がり、ロゴをチラ見せして上へ抜ける
   - 次ページのコンテンツがフワッとフェードイン
   - Shopify特有のJS再発火、アナリティクス、スクロールリセットに対応
   ========================================================================== */

(function () {
  'use strict';

  // ------------------------------------------------------------------
  // 1. 起動条件のチェック
  // ------------------------------------------------------------------

  // テーマエディタのプレビュー画面では動作させない（Shopify UIとの競合回避）
  if (window.Shopify && window.Shopify.designMode) {
    return;
  }

  // ライブラリの読み込み待ち（deferでも稀に順序がずれるケースを保険）
  function ready() {
    if (typeof window.barba === 'undefined' || typeof window.gsap === 'undefined') {
      return window.setTimeout(ready, 50);
    }
    init();
  }

  // 動きを抑えたいユーザー向けに、duration を短縮する係数
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
  const SPEED = prefersReducedMotion ? 0.3 : 1;

  // ------------------------------------------------------------------
  // 2. Barba 初期化
  // ------------------------------------------------------------------

  function init() {
    const overlay = document.querySelector('.page-transition');
    const logo = document.querySelector('.page-transition__logo');

    if (!overlay || !logo) {
      console.warn('[barba] .page-transition が見つかりません。snippets/page-transition.liquid を <body> 直下に配置してください。');
      return;
    }

    barba.init({
      debug: false,
      timeout: 5000,

      // ----- 除外条件 -----
      prevent: ({ el, event, href }) => {
        if (!el) return false;

        // 明示的に除外したいリンク
        if (el.classList && el.classList.contains('no-barba')) return true;
        if (el.hasAttribute('data-no-barba')) return true;

        // 別タブで開くリンクは遷移演出と相性が悪い
        if (el.getAttribute('target') === '_blank') return true;

        // download 属性付きは遷移ではなくファイル取得
        if (el.hasAttribute('download')) return true;

        // mailto / tel など外部スキーム
        const url = el.getAttribute('href') || href || '';
        if (/^(mailto:|tel:|sms:|javascript:)/i.test(url)) return true;

        // 同一ページ内アンカー
        if (url.startsWith('#')) return true;

        // 外部ドメイン
        try {
          const linkUrl = new URL(url, window.location.href);
          if (linkUrl.origin !== window.location.origin) return true;
        } catch (e) {
          return true;
        }

        // Shopifyの認証/チェックアウト/カート系は標準遷移に任せる
        if (/^\/(cart|checkout|account|orders|password|challenge|admin)/i.test(
          new URL(url, window.location.href).pathname
        )) {
          return true;
        }

        // Cmd/Ctrl/Shift クリック（別タブ・別ウィンドウ）はブラウザ標準動作
        if (event && (event.metaKey || event.ctrlKey || event.shiftKey)) {
          return true;
        }

        return false;
      },

      // ----- トランジション定義 -----
      transitions: [
        {
          name: 'overlay-transition',

          // ----- 離脱: 幕が下から上へ覆う -----
          leave(data) {
            const done = this.async();

            document.documentElement.classList.add('is-transitioning');

            const tl = gsap.timeline({ onComplete: done });

            tl.set(overlay, { y: '100%' })
              .set(logo, { opacity: 0, y: 30 })
              .to(overlay, {
                y: '0%',
                duration: 0.8 * SPEED,
                ease: 'power4.inOut',
              })
              .to(
                logo,
                {
                  opacity: 1,
                  y: 0,
                  duration: 0.4 * SPEED,
                  ease: 'power2.out',
                },
                '-=0.45'
              );
          },

          // ----- 進入: 幕が上へ抜け、新コンテンツがフェードイン -----
          enter(data) {
            const done = this.async();

            // 新しいコンテナを最初は透明＋少し下に
            gsap.set(data.next.container, { opacity: 0, y: 30 });

            const tl = gsap.timeline({
              onComplete: () => {
                document.documentElement.classList.remove('is-transitioning');
                done();
              },
            });

            tl.to(overlay, {
              y: '-100%',
              duration: 0.8 * SPEED,
              ease: 'power4.inOut',
              delay: 0.15 * SPEED, // ロゴをちらりと見せる「間」
            })
              .to(
                data.next.container,
                {
                  opacity: 1,
                  y: 0,
                  duration: 0.6 * SPEED,
                  ease: 'power2.out',
                },
                '-=0.5'
              )
              // 次の遷移に備えて初期位置へリセット
              .set(overlay, { y: '100%' })
              .set(logo, { opacity: 0, y: 30 });
          },
        },
      ],
    });

    // ------------------------------------------------------------------
    // 3. Hooks: Shopifyとの連携
    // ------------------------------------------------------------------

    // 新ページ進入の直前（DOM差し替え直後）
    barba.hooks.beforeEnter((data) => {
      window.scrollTo(0, 0);
    });

    // 進入アニメーションの完了後（ここで再発火）
    barba.hooks.after((data) => {
      // 1) アナリティクス
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib) {
        try {
          window.ShopifyAnalytics.lib.page();
        } catch (e) {
          /* noop */
        }
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_location: window.location.href,
          page_path: window.location.pathname,
          page_title: document.title,
        });
      }

      // 2) <title> の更新（Barbaが対応していないSEOメタの追従用フック）
      const nextTitle = data.next.html.match(/<title[^>]*>([^<]*)<\/title>/i);
      if (nextTitle && nextTitle[1]) {
        document.title = nextTitle[1];
      }

      // 3) カスタムエレメントの再認識
      //    Dawn系テーマは独自のCustom Elementで動的UIを構築しているため、
      //    新DOMに対してアップグレード処理を流す。
      if (window.customElements && typeof window.customElements.upgrade === 'function') {
        try {
          window.customElements.upgrade(data.next.container);
        } catch (e) {
          /* noop */
        }
      }

      // 4) Shopify Section Rendering API で動くUI（カートドロワーなど）の再初期化
      //    テーマ独自の初期化関数がある場合はここで呼び出します。
      //    例:
      //      if (typeof window.initSliders === 'function') window.initSliders();
      //      if (typeof window.initAccordions === 'function') window.initAccordions();
      //      document.dispatchEvent(new CustomEvent('page:loaded'));

      // 5) フォーカスをメインへ（アクセシビリティ）
      const main = document.querySelector('#MainContent');
      if (main) main.focus({ preventScroll: true });
    });

    // 遷移エラー時のフォールバック（通信失敗など）
    barba.hooks.afterLeave(() => {
      // タイムアウトで止まった場合に備え、クラスは必ず剥がす
      window.setTimeout(() => {
        document.documentElement.classList.remove('is-transitioning');
      }, 2000);
    });
  }

  // DOMContentLoaded を待ってから起動
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }
})();
